import React, { useEffect, useState, useCallback } from "react";
import { Rocket, Wrench, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";

/**
 * Global site mode handler:
 * - "live" → renders nothing
 * - "prelaunch" → countdown banner at top of every page (anyone can browse, purchases blocked)
 * - "maintenance" → full-screen overlay blocking the entire site (except admins)
 * Polls /settings every 60s so changes from admin take effect without reload.
 */
export default function SiteModeBanner() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [cfg, setCfg] = useState(null);
    const [now, setNow] = useState(Date.now());

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/settings");
            setCfg({
                mode: (data.site_mode || "live").toLowerCase(),
                launch_date: data.launch_date || "",
                launch_title: data.launch_title || t("Próximo lanzamiento", "Coming soon"),
                launch_message: data.launch_message || t("Estamos preparando algo especial. Vuelve pronto.", "We're preparing something special. Come back soon."),
                maintenance_message: data.maintenance_message || t("Estamos actualizando el sitio. Volvemos en unos minutos.", "We're updating the site. We'll be back in a few minutes."),
            });
        } catch {
            setCfg(null);
        }
    }, [t]);

    useEffect(() => {
        load();
        const poll = setInterval(load, 60000);
        return () => clearInterval(poll);
    }, [load]);

    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, []);

    if (!cfg) return null;

    const isAdmin = user?.role === "admin";

    // ============ Maintenance Overlay ============
    if (cfg.mode === "maintenance" && !isAdmin) {
        return (
            <div className="fixed inset-0 z-[100000] bg-[var(--tb-bg)] flex items-center justify-center p-6" data-testid="maintenance-overlay">
                <div className="max-w-lg text-center space-y-5">
                    <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                        <Wrench className="w-10 h-10 text-amber-400" />
                    </div>
                    <h1 className="font-display text-4xl text-[var(--tb-gold)]">{t("Sitio en mantenimiento", "Site under maintenance")}</h1>
                    <p className="text-[var(--tb-text-2)] text-base leading-relaxed">{cfg.maintenance_message}</p>
                    <div className="text-xs text-[var(--tb-muted)]">— Terra Boutique</div>
                </div>
            </div>
        );
    }

    // ============ Prelaunch Countdown Banner ============
    if (cfg.mode === "prelaunch") {
        const target = cfg.launch_date ? Date.parse(cfg.launch_date) : 0;
        const diff = target ? Math.max(0, target - now) : 0;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const mins = Math.floor((diff / (1000 * 60)) % 60);
        const secs = Math.floor((diff / 1000) % 60);
        const adminNote = isAdmin ? t(" · Modo admin: las compras siguen habilitadas para ti.", " · Admin mode: purchases remain enabled for you.") : "";
        return (
            <div className="bg-gradient-to-r from-[var(--tb-gold)]/20 via-[var(--tb-border-gold)]/15 to-[var(--tb-gold)]/20 border-b border-[var(--tb-border-gold)]/40"
                data-testid="prelaunch-banner">
                <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-center sm:text-left">
                    <Rocket className="w-5 h-5 text-[var(--tb-gold)] flex-shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-display text-sm sm:text-base text-[var(--tb-gold)]">{cfg.launch_title}</div>
                        <p className="text-[10px] sm:text-xs text-[var(--tb-text-2)] mt-0.5">{cfg.launch_message}{adminNote}</p>
                    </div>
                    {target > 0 && (
                        <div className="flex items-center gap-1.5 text-xs sm:text-sm font-mono" data-testid="prelaunch-countdown">
                            <Clock className="w-4 h-4 text-[var(--tb-gold)]" />
                            <span className="bg-[var(--tb-bg-2)]/60 border border-[var(--tb-border)] rounded px-2 py-1">
                                <strong className="text-[var(--tb-gold)]">{days}</strong> {t("días", "days")}
                            </span>
                            <span className="bg-[var(--tb-bg-2)]/60 border border-[var(--tb-border)] rounded px-2 py-1">
                                <strong className="text-[var(--tb-gold)]">{String(hours).padStart(2, "0")}</strong> h
                            </span>
                            <span className="bg-[var(--tb-bg-2)]/60 border border-[var(--tb-border)] rounded px-2 py-1">
                                <strong className="text-[var(--tb-gold)]">{String(mins).padStart(2, "0")}</strong> m
                            </span>
                            <span className="bg-[var(--tb-bg-2)]/60 border border-[var(--tb-border)] rounded px-2 py-1">
                                <strong className="text-[var(--tb-gold)]">{String(secs).padStart(2, "0")}</strong> s
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return null;
}

/**
 * Lightweight hook for any component that needs to know if purchases are blocked.
 * Polls /settings (same cache).
 */
export function useSiteMode() {
    const [mode, setMode] = useState("live");
    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/settings");
                setMode((data.site_mode || "live").toLowerCase());
            } catch { /* ignore */ }
        };
        load();
        const poll = setInterval(load, 60000);
        return () => clearInterval(poll);
    }, []);
    return mode;
}
